import { Component, Input, OnChanges } from "@angular/core";
import { StateService } from "@uirouter/core";
import { sum } from "../../utils/sum";

@Component({
  selector: "ab-response-code-switch",
  templateUrl: "./response-code-switch.component.html",
})
export class ResponseCodeSwitchComponent implements OnChanges {
  @Input() public statusCode: any;
  @Input() public routeStats: any;

  public responseCodes: any;
  public currentStatus: any;

  private stdCodes = [
    { text: "All response codes", value: "total" },
    { text: "2xx", value: "success" },
    { text: "3xx", value: "redirection" },
    { text: "4xx", value: "clientError" },
    { text: "5xx", value: "serverError" },
  ];

  private statusCodeTexts: any = {
    200: "OK",
    201: "Created",
    204: "No Content",
    301: "Moved Permanently",
    302: "Found",
    308: "Permanent Redirect",
    400: "Bad Request",
    401: "Unauthorized",
    403: "Forbidden",
    404: "Not Found",
    406: "Not Acceptable",
    408: "Request Timeout",
    410: "Gone",
    413: "Request Entity Too Large",
    420: "Enhance Your Calm",
    422: "Unprocessable Entity",
    429: "Too Many Requests",
    500: "Internal Server Error",
    502: "Bad Gateway",
    504: "Gateway Timeout",
  };

  constructor(private $state: StateService) {}

  public ngOnChanges() {
    if (!this.routeStats[0] || !this.statusCode) {
      return;
    }

    this.responseCodes = this.statusOptions();

    for (const code of this.responseCodes) {
      if (code.value === this.statusCode.name) {
        this.currentStatus = code;
        break;
      }
    }

    if (!this.currentStatus) {
      this.currentStatus = this.responseCodes[0];
    }
  }

  public trackByIndex(index: number, _item: any) {
    return index;
  }

  public selectResponseStatus(statusCode: any) {
    this.$state.go(".", { status: statusCode.value });
  }

  // Count how many times a code of a certain group is within `codes`.
  // Example:
  //   const codes = {400: ..., 401: ..., 300: ...};
  //   countCodeGroups(codes); // {4: 2, 3: 1}
  private countCodeGroups(codes: any) {
    const groups: any = { t: 2 }; // Always show "total".

    for (const code of Object.keys(codes)) {
      const groupId = code[0];
      if (!groups[groupId]) {
        groups[groupId] = 0;
      }
      groups[groupId]++;
    }

    return groups;
  }

  // Filter out `stdOpts` based on provided `codes`. A standard option is
  // omitted when `codes` has only one status code per group.
  private filterStdOpts(stdOpts: any, codes: any) {
    const groups = this.countCodeGroups(codes);

    for (const route of this.routeStats) {
      for (const opt of stdOpts) {
        if (!route[opt.value] || groups[opt.name[0]] < 2) {
          continue;
        }

        let count = route[opt.value].count;
        if (Array.isArray(count)) {
          count = sum(count);
        }
        if (count > 0) {
          opt.count++;
        }
      }
    }

    return stdOpts.filter((v: any) => v.count > 0);
  }

  // Return an object that holds all status code options for a certain route.
  private statusOptions() {
    let stdOpts = [];

    // Build group options (2xx, 3xx, 4xx, 5xx).
    for (const code of this.stdCodes) {
      stdOpts.push({ ...code, name: code.text, count: 0 });
    }

    const codes = this.buildStatusCodes();

    // Filter out stdOpts if `codes` contains only 1 code per group.
    stdOpts = this.filterStdOpts(stdOpts, codes);

    // Build the opts array.
    const customOpts = [];
    for (const k of Object.keys(codes)) {
      customOpts.push(codes[k]);
    }

    // Make sure it's sorted.
    customOpts.sort((a, b) => {
      if (a.value < b.value) {
        return -1;
      }
      if (a.value > b.value) {
        return 1;
      }
      return 0;
    });

    // Finalize the list of options.
    const opts: any = stdOpts.concat(customOpts);
    for (const opt of opts) {
      opt.statusCodeText = this.statusCodeTexts[opt.value] || "";
      opt.text = opt.text;

      if (opt.statusCodeText) {
        opt.text += ` ${opt.statusCodeText}`;
      }
      if (opts[0].count > 1) {
        opt.text += ` (${opt.count})`;
      }
    }

    return opts;
  }

  // Build options that correspond to status codes found within a route
  // (200, 404, 500, etc).
  private buildStatusCodes(): any {
    const codes: any = {};

    for (const route of this.routeStats) {
      for (const code of Object.keys(route.statusCodes)) {
        if (codes[code]) {
          codes[code].count++;
          continue;
        }
        codes[code] = { name: code, text: code, value: code, count: 1 };
      }
    }

    return codes;
  }
}
